import { ProductCapabilities } from '@awork/_shared/services/product/product-capabilities.service'
import { environment, apiEndpoint, app } from '@awork/environments/environment'
import { DestroyRef, Injectable, effect, inject } from '@angular/core'
import { Location } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { get as getCookie, set as setCookie } from 'es-cookie'
import { SubscriptionQuery } from '@awork/_shared/state/subscription.query'
import { AccountQuery } from '@awork/_shared/state/account.query'
import type { AccountState } from '@awork/_shared/models/account.model'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import { Workspace } from '@awork/features/workspace/models/workspace.model'
import type { Observable, Subscription } from 'rxjs'
import { take, map, forkJoin, filter, firstValueFrom } from 'rxjs'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { AppQuery } from '@awork/core/state/app.query'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { Level } from '@awork/_shared/services/log-service/types'
import { ErrorType } from '@awork/_shared/functions/classify-error'
import { QSubscription } from '@awork/_shared/models/subscription.model'
import { BrowserService } from '@awork/_shared/services/browser-service/browser.service'
import getLanguage from '@awork/_shared/functions/get-language'
import { differenceInDays, differenceInYears } from '@awork/_shared/functions/date-fns-wrappers'
import { QEventService } from '@awork/_shared/services/q-event-service/q-event.service'
import { UserQuery } from '@awork/features/user/state/user.query'
import { ABTestService } from '@awork/_shared/services/ab-test-service/ab-test.service'
import type { Params } from '@angular/router'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import getHostname from '@awork/_shared/functions/get-hostname'
import { TrackingEvent } from './events'
import { PermissionsQuery } from '@awork/features/workspace/state/permissions.query'
import { randomInt } from '@awork/core/mocks_stubs/mock.helpers'
import {
  buildMarketingPropertiesFromAwUrlCookieOrUrl,
  buildQueryParamsWithoutTrackingParams,
  hasTrackingQueryParams
} from './marketing-params-helpers'
import { SignalStoreService } from '@awork/core/state/signal-store/signalStore.service'
import type { Analytics } from '@segment/analytics-next'
import { object } from 'lib0'
import isTest from '@awork/_shared/functions/is-test'
import { ensureGoogleConsentModeDefaults } from './google-consent-mode'
import { FeatureFlagQuery } from '@awork/_shared/state/feature-flag.query'
import { FeatureFlag } from '@awork/_shared/state/feature-flag.store'
import { BIRDIE_CLIENT_ID } from './birdie.constants'
import { intercomIntegrationsDisabled, loadSegment } from './segment-loader'
import initializeMessenger from '@intercom/messenger-js-sdk'
import { initBirdieSnippet } from '@awork/_shared/functions/birdie-snippet'

interface SupportTokenResponse {
  token: string
  userId: string
  workspaceId: string
  expiresAt: number
}

/** Why a token request gave no token: `retry` can recover by itself, `stop` waits for the next identity change. */
type SupportTokenResult = { kind: 'token'; token: SupportTokenResponse } | { kind: 'retry' } | { kind: 'stop' }

export type SupportChatResult = 'opened' | 'unavailable' | 'blocked'

const INTERCOM_FIRST_RETRY_DELAY = 60_000
// The normal token refresh interval, so a failing user never asks more often than a healthy one in the long run.
const INTERCOM_MAX_RETRY_DELAY = 600_000
// The Messenger SDK adds its widget script with this id. A failed script load fires `error` on it.
const INTERCOM_LOADER_SCRIPT_ID = '_intercom_npm_loader'

declare const Intercom: (command: string, payload?: Record<string, unknown>) => void

export enum TrackingSegment {
  NpsRating = '6290c11f131af619b3bb9282'
}

@Injectable({ providedIn: 'root' })
export class TrackingService {
  private readonly product = inject(ProductCapabilities)
  private readonly destroyRef = inject(DestroyRef)
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  private location = inject(Location)
  private accountQuery = inject(AccountQuery)
  private workspaceQuery = inject(WorkspaceQuery)
  private subscriptionQuery = inject(SubscriptionQuery)
  private appQuery = inject(AppQuery)
  private apiClient = inject(ApiClient)
  private logService = inject(LogService)
  private browserService = inject(BrowserService)
  private userQuery = inject(UserQuery)
  private qEventService = inject(QEventService)
  private permissionsQuery = inject(PermissionsQuery)
  private abTestService = inject(ABTestService)
  private signalStoreService = inject(SignalStoreService)
  private featureFlagQuery = inject(FeatureFlagQuery)

  analytics: Analytics
  account = this.accountQuery.queryAccountState()
  workspace = this.workspaceQuery.queryCurrentWorkspace()
  private shouldInitializeBirdie = this.featureFlagQuery.queryFlag(FeatureFlag.initializeBirdie)
  private hasInitializedBirdie = false

  private trackAppAccessedSubscription: Subscription
  private trackingScriptLoadPromise?: Promise<void>
  private isTrackingScriptLoaded = false
  private readonly isLoggingSample = randomInt(50, 1) === 1
  private readonly intercomSettings = {
    app_id: this.product.tracking.intercomAppId,
    hide_default_launcher: true
  }
  private isIntercomAnonymous = false
  private intercomUserJwt?: SupportTokenResponse
  private intercomUserJwtPromise?: { userId: string; promise: Promise<SupportTokenResult> }
  private intercomIdentityVersion = 0
  private intercomBrowserAttributes: Record<string, unknown> = {}
  private intercomUserId?: string
  private intercomTargetUserId?: string
  private intercomTargetWorkspaceId?: string
  private intercomRefreshTimer?: ReturnType<typeof setTimeout>
  private intercomRetryDelay = INTERCOM_FIRST_RETRY_DELAY
  private intercomTokenPausedUntil = 0
  private intercomScriptFailure?: Exclude<SupportChatResult, 'opened'>
  private readonly loggedIntercomTokenFailures = new Set<string>()
  private currentUser = this.userQuery.queryCurrentUser()
  private isLoggedIn = this.appQuery.queryIsLoggedIn()

  constructor() {
    this.getMarketingParams()
    this.initTracking()
    this.initTrackingOnSignUp()
    this.destroyRef.onDestroy(() => this.clear())
    this.watchIntercomScript()
    effect(() => {
      const userId = this.currentUser()?.id
      const workspaceId = this.workspace()?.id
      const isLoggedIn = this.isLoggedIn()
      const isImpersonated = this.account()?.isImpersonated

      if (
        this.intercomTargetUserId &&
        (userId !== this.intercomTargetUserId ||
          workspaceId !== this.intercomTargetWorkspaceId ||
          !isLoggedIn ||
          isImpersonated)
      ) {
        this.clearIntercomIdentity()
        this.intercomTargetUserId = undefined
      }
    })

    effect(() => {
      this.initBirdieSnippet()
    })
  }

  /**
   * Gets the marketing parameters from the url and sets the marketing cookies
   */
  private getMarketingParams(): void {
    const url = new URL(window.location.href)
    const searchParams = new URLSearchParams(url.search)
    const shouldRemoveTrackingParams = hasTrackingQueryParams(searchParams)

    // Base64 encoded param
    if (searchParams.get('aw_ids')) {
      try {
        const decodedParam = atob(searchParams.get('aw_ids'))
        const decodedSearchParams = new URLSearchParams(decodedParam)

        for (const [key, value] of decodedSearchParams.entries()) {
          searchParams.set(key, value)
        }
      } catch (_) {}
    }

    this.setMarketingCookies(searchParams)

    if (shouldRemoveTrackingParams) {
      this.setQueryParams(searchParams)
    }
  }

  /**
   * Sets the decoded query params in the url
   * @param {URLSearchParams} searchParams
   */
  private setQueryParams(searchParams: URLSearchParams): void {
    if (!searchParams.size) {
      return
    }

    const queryParams: Params = buildQueryParamsWithoutTrackingParams(searchParams)

    // The current path is needed to prevent the router from navigating to the root route (/)
    const currentPath = window.location.pathname

    // This service is created during bootstrap, before the router runs its first navigation.
    // A router navigation at that point finishes before the app component subscribes to router
    // events, and the router skips its own first navigation. The startup loader then never goes away.
    // Rewrite the browser URL directly, so the first navigation uses the clean URL.
    if (!this.router.navigated) {
      this.location.replaceState(currentPath, new URLSearchParams(queryParams).toString())

      return
    }

    this.router.navigate([currentPath], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    })
  }

  /**
   * Sets marketing cookies based on url parameters
   * @param {URLSearchParams} params
   */
  private setMarketingCookies(params: URLSearchParams): void {
    const hostname = getHostname()

    // Google
    if (params.get('gclid')) {
      setCookie('aw_gclid', params.get('gclid'), {
        domain: hostname
      })
    }

    // LinkedIn
    if (params.get('li_fat_id')) {
      setCookie('aw_li_fat_id', params.get('li_fat_id'), {
        domain: hostname
      })
    }

    // Facebook
    if (params.get('fbclid')) {
      setCookie('aw_fbclid', params.get('fbclid'), {
        domain: hostname
      })
    }

    // Facebook
    if (params.get('fbc')) {
      setCookie('aw_fbc', params.get('fbc'), {
        domain: hostname
      })
    }

    // Outbrain
    if (params.get('obclid')) {
      setCookie('aw_obclid', params.get('obclid'), {
        domain: hostname
      })
    }

    // Segment
    if (params.get('uid')) {
      setCookie('aw_ajs_user_id', params.get('uid'), {
        domain: hostname
      })
    } else {
      const ajsCookie = getCookie('ajs_user_id')

      if (ajsCookie) {
        setCookie('aw_ajs_user_id', ajsCookie, {
          domain: hostname
        })
      }
    }
  }

  /**
   * Initializes the tracking providers (Segment and Google) according to the workspace subscription
   * @private
   */
  private initTracking(): void {
    this.trackAppAccessedSubscription = forkJoin({
      app: this.signalStoreService.selectOnStoreRestored('app'),
      workspace: this.signalStoreService.selectOnStoreRestored('workspace'),
      subscription: this.signalStoreService.selectOnStoreRestored('subscription')
    }).subscribe(() => {
      const isLoggedIn = this.appQuery.getIsLoggedIn()

      // If the user is logged in we can determine if paid plan from the subscription
      // Otherwise, we can rely on the workspace (coming from workspace by subdomain)
      const subscription = this.subscriptionQuery.querySubscription()
      const subscriptionInfo = isLoggedIn && !!subscription().planId ? subscription() : this.workspace()

      try {
        let integrations: Record<string, boolean>

        const isLoggedInPaidSubscription =
          subscriptionInfo instanceof QSubscription &&
          this.subscriptionQuery.isPaidPlan() &&
          subscriptionInfo.status === 'Active'

        const isWorkspacePaidSubscription = subscriptionInfo instanceof Workspace && subscriptionInfo.isPaidPlan

        if (isLoggedInPaidSubscription || isWorkspacePaidSubscription) {
          integrations = { All: false, Intercom: true, Adtriba: true }
        } else {
          integrations = { All: true }
        }

        this.loadTrackingScript(integrations)
      } catch (error) {
        if (error instanceof Error) {
          this.logService.logError(error)
        }
      }
    })
  }

  /**
   * Initializes the tracking on sign-up form with all the Segment's integrations
   */
  private initTrackingOnSignUp(): void {
    this.router.events
      ?.pipe(
        filter(event => event instanceof NavigationEnd && (event.urlAfterRedirects || event.url).includes('/signup')),
        take(1)
      )
      .subscribe(() => {
        this.loadTrackingScript({ All: true })
      })
  }

  /**
   * Initializes the Birdie snippet to access extra information during recordings
   */
  private initBirdieSnippet(): void {
    if (
      this.hasInitializedBirdie ||
      environment !== 'production' ||
      isTest() ||
      this.isRecorderRoute() ||
      !this.shouldInitializeBirdie() ||
      app === 'mobile'
    ) {
      return
    }

    const currentUser = this.userQuery.queryCurrentUser()
    const workspace = this.workspaceQuery.queryCurrentWorkspace()
    const user = currentUser()

    if (!user?.contactEmail) {
      return
    }

    initBirdieSnippet({
      clientId: BIRDIE_CLIENT_ID,
      contact: {
        email: user.contactEmail,
        name: user.fullName,
        id: user.id
      },
      onReady(birdie) {
        birdie.on('start', () => {
          birdie.metadata = {
            user: {
              id: currentUser()?.id,
              email: currentUser()?.contactEmail,
              name: currentUser()?.fullName
            },
            workspace: {
              id: workspace()?.id,
              name: workspace()?.name,
              subdomain: workspace()?.getDefaultSubdomain()
            }
          }
        })
      }
    })
    this.hasInitializedBirdie = true
  }

  /**
   * Determines whether the current path is the dedicated Birdie recorder page.
   * @returns {boolean}
   */
  private isRecorderRoute(): boolean {
    const normalizedPathname = window.location.pathname.replace(/\/+$/, '') || '/'

    return normalizedPathname === '/recorder'
  }

  /**
   * Ensures tracking providers are initialized once in production by loading Segment and booting Intercom.
   * Concurrent callers share the same in-flight initialization to prevent duplicate loads.
   * @param {Record<string, boolean>} integrations Segment integration flags used for initialization.
   * @param {number} [retryCount=0] Current retry attempt (0 = first attempt).
   * @returns {Promise<void>}
   */
  private async loadTrackingScript(integrations: Record<string, boolean>, retryCount = 0): Promise<void> {
    // Only load the tracking script in production and never initialize it more than once.
    if (environment !== 'production' || this.isTrackingScriptLoaded) {
      return
    }

    // Google requires consent defaults before tags initialize via Segment.
    ensureGoogleConsentModeDefaults()

    // If another caller is already loading the script, wait for that attempt.
    if (this.trackingScriptLoadPromise) {
      await this.trackingScriptLoadPromise

      return
    }

    this.invokeWhenActive(() => void this.initializeIntercom())
    this.trackingScriptLoadPromise = this.initializeTrackingProviders(integrations, retryCount)

    try {
      await this.trackingScriptLoadPromise
    } finally {
      // Allow a future retry if loading did not complete successfully.
      if (!this.isTrackingScriptLoaded) {
        this.trackingScriptLoadPromise = undefined
      }
    }
  }

  /**
   * Loads Segment and waits for readiness. Retries once after a delay on the first failure.
   * @param {Record<string, boolean>} integrations Segment integration flags used for initialization.
   * @param {number} [retryCount=0] Current retry attempt (0 = first attempt).
   * @returns {Promise<void>}
   */
  private async initializeTrackingProviders(integrations: Record<string, boolean>, retryCount = 0): Promise<void> {
    const writeKey = this.product.tracking.segmentWriteKey
    const retryDelayMs = 5000

    try {
      if (!this.analytics) {
        this.analytics = await loadSegment(writeKey, integrations)
      }

      await this.analytics.ready()

      this.isTrackingScriptLoaded = true
    } catch (error) {
      // Retry once after delay if this is the first attempt
      if (retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, retryDelayMs))
        await this.initializeTrackingProviders(integrations, retryCount + 1)
      }
    }
  }

  invokeWhenActive(callback: Function) {
    try {
      if (
        // disable tracking for:
        // 1. non-production environments
        (environment !== 'production' && environment !== 'staging') ||
        // 2. impersonated account sessions
        this.account()?.isImpersonated ||
        // 3. end-to-end tests
        getCookie('aw_test')
      ) {
        // tracking disabled
        // *do nothing*
      } else {
        // tracking enabled
        callback()
      }
    } catch (error) {
      if (error instanceof Error) {
        this.logService.logError(error)
      }
    }
  }

  trackEvent(eventName: TrackingEvent, properties = {}, callback = () => {}, userId?: string) {
    this.invokeWhenActive(() => {
      this.trackToLinkedIn(eventName, this.workspace()?.id)

      const isOnboardingCompletedEvent = eventName === TrackingEvent.onboardingCompleted

      // Segment
      try {
        const subscription = this.subscriptionQuery.getSubscription()
        const eventDataProperties = this.buildEventDataProperties(properties, isOnboardingCompletedEvent, subscription)

        this.analytics?.track(
          eventName,
          eventDataProperties,
          {
            integrations: {
              All: true,
              ...intercomIntegrationsDisabled, // Intercom events are tracked by the backend.
              'Google Analytics': this.includeEventForDestination(eventName, 'Google Analytics'),
              'Facebook Pixel': this.includeEventForDestination(eventName, 'Facebook Pixel')
            }
          },
          callback
        )

        // Track events in parallel via our own backend (to avoid ad-blockers)
        // ---
        // Currently used for mixpanel mainly. Some events need to be excluded, because they
        // are being tracking by the backend already as well. Otherwise the are in there twice.
        if (
          !['Signed Up', 'Trial Started', 'Invitation Accepted'].includes(eventName) &&
          this.appQuery.getIsLoggedIn()
        ) {
          this.trackEventCall(eventName, eventDataProperties, userId).subscribe()
        }

        if (isOnboardingCompletedEvent) {
          this.logService.logMessage(Level.info, 'Onboarding Completed')
        }
      } catch (err) {}
    })

    if (this.isDeveloperEnvironment()) {
      console.log(eventName, properties)

      if (callback) {
        callback()
      }
    }
  }

  /**
   * Gets the workspace AI status for event properties. An unknown value stays absent so reports never read it as false.
   * @param {QSubscription | undefined} subscription
   * @returns {Record<string, boolean>}
   */
  private getAiStatusProperties(subscription: QSubscription | undefined): Record<string, boolean> {
    const properties: Record<string, boolean> = {}

    if (subscription?.aiPaidPlanActive != null) {
      properties.ai_paid_plan_active = subscription.aiPaidPlanActive
    }

    if (subscription?.aiTrialActive != null) {
      properties.ai_trial_active = subscription.aiTrialActive
    }

    return properties
  }

  /**
   * Builds the event data properties
   * @param {object} properties
   * @param {boolean} isOnboardingCompletedEvent
   * @param {QSubscription | undefined} subscription
   * @returns
   */
  private buildEventDataProperties(
    properties: object,
    isOnboardingCompletedEvent: boolean,
    subscription: QSubscription | undefined
  ) {
    return {
      ...properties,
      Category: 'awork',
      Device: 'web',
      os: this.browserService.getOS(),
      osVersion: this.browserService.getOSVersion(),
      id: this.workspace()?.id || null,
      messageId: this.workspace()?.id || null,
      company: this.workspace()?.id || null,
      subscription_plan: subscription?.getPlan() || null,
      subscription_status: subscription?.status || null,
      in_trial: this.subscriptionQuery.isInTrial(),
      in_basic_connect: subscription?.isConnectPlan,
      in_connect_business_trial: subscription?.isBasicConnectInTrial,
      ...this.getAiStatusProperties(subscription),
      ...(isOnboardingCompletedEvent ? { order_id: this.workspace()?.id || null } : {}),
      ...(isOnboardingCompletedEvent ? this.getMarketingTrackingContext() : {})
    }
  }

  /**
   * List of exclusions for tracking destinations in segment
   * @param {string} event
   * @param {string} destination
   * @returns {boolean}
   */
  includeEventForDestination(event: string, destination: string): boolean {
    let excludedEvents: string[] = []

    switch (destination) {
      case 'Mixpanel':
        excludedEvents = ['Signed Up', 'Trial Started']

        if (excludedEvents.includes(event)) {
          return false
        }

        break
      case 'Intercom':
        break
      case 'Google Analytics':
        excludedEvents = ['Signed Up', 'Trial Started']

        if (excludedEvents.includes(event)) {
          return false
        }

        break
      case 'Facebook Pixel':
        excludedEvents = ['Signed Up', 'Onboarding Completed']

        if (excludedEvents.includes(event)) {
          return false
        }

        break
    }

    return true
  }

  /**
   * Tracks events to linkedin but only onboarding complete and signed up right now.
   * @param {string} eventName
   * @param {string} workspaceId
   */
  trackToLinkedIn(eventName: string, workspaceId: string): void {
    try {
      const lintrk = (window as unknown as { lintrk?: unknown }).lintrk

      if (lintrk instanceof Function) {
        if (eventName == 'Onboarding Completed') {
          for (const conversionId of this.product.tracking.linkedInOnboardingCompletedConversionIds) {
            lintrk('track', { conversion_id: conversionId, event_id: workspaceId })
          }
        } else if (eventName == 'Signed Up') {
          for (const conversionId of this.product.tracking.linkedInSignedUpConversionIds) {
            lintrk('track', { conversion_id: conversionId, event_id: workspaceId })
          }
        }
      }
    } catch (err) {
      this.logService.logError(new Error(`LinkedIn tracking: ${err}`))
    }
  }

  trackPage(url: string) {
    this.invokeWhenActive(() => {
      this.analytics?.page({ properties: { url } }, { integrations: intercomIntegrationsDisabled })
      void this.initializeIntercom()
    })
  }

  setAlias(id: string) {
    this.invokeWhenActive(() => {
      this.analytics?.alias(id)
      // this.analytics.flush()
    })
  }

  identifyUser(userId: string, userProperties: Record<string, unknown> = {}, callback = () => {}) {
    this.invokeWhenActive(() => {
      // Messenger failure must not delay analytics or the signup callback.
      this.analytics?.identify(userId, userProperties, { integrations: intercomIntegrationsDisabled }, callback)
      this.identifyUserCall(userProperties).subscribe()
      this.intercomTokenPausedUntil = 0
      void this.identifyIntercomUser(userId, userProperties)
    })

    if (this.isDeveloperEnvironment()) {
      console.log('Identify User', userId, userProperties)

      if (callback) {
        callback()
      }
    }
  }

  identifyGroup(groupId: string, groupProperties = {}) {
    this.invokeWhenActive(() => {
      this.analytics?.group(groupId, groupProperties, { integrations: intercomIntegrationsDisabled })

      if (groupId === this.workspace()?.id) {
        this.intercomUserJwt = undefined
        this.intercomTokenPausedUntil = 0
        void this.initializeIntercom()
      }

      const groupDataProperties = {
        // needed for the mixpanel company consolidation
        ...groupProperties,
        company: this.workspace()?.id || null
      }

      this.identifyGroupCall(groupDataProperties).subscribe()
    })
  }

  /**
   * Identify Group API Call
   * @param {object} data
   * @returns {Observable<void>}
   */
  identifyGroupCall(data: object): Observable<void> {
    const body = {
      data,
      context: this.setEventContextData()
    }
    return this.apiClient.post(`${apiEndpoint}/group`, body)
  }

  /**
   * Identify User API Call
   * @param {object} data
   * @returns {Observable<void>}
   */
  identifyUserCall(data: object): Observable<void> {
    const body = {
      data,
      context: this.setEventContextData()
    }

    return this.apiClient.post(`${apiEndpoint}/identify`, body)
  }

  resetDistinctId() {
    this.invokeWhenActive(() => {
      this.analytics?.reset()
    })
  }

  flush() {
    this.invokeWhenActive(() => {
      // Note: flush() is not exposed in @segment/analytics-next
      // The library automatically batches and flushes events
      // For manual flushing, we can trigger a page event as a workaround
      if (this.analytics) {
        // Workaround: trigger a page event to force a flush
        this.analytics.page({}, { integrations: intercomIntegrationsDisabled })
        void this.initializeIntercom()
      }
    })
  }

  /**
   * Clears the identification state of the user (e.g when loging out)
   */
  clear(): void {
    this.clearIntercomIdentity()
    this.intercomTargetUserId = undefined
    this.loggedIntercomTokenFailures.clear()
    this.analytics?.reset()
  }

  /**
   * Opens the support chat if Messenger runs with a signed identity and its script loaded.
   * @returns {SupportChatResult} `blocked` when the Messenger script did not load and an ad blocker or a network filter may stop it
   */
  openSupportChat(): SupportChatResult {
    if (!this.intercomUserId) {
      return 'unavailable'
    }

    if (this.intercomScriptFailure) {
      return this.intercomScriptFailure
    }

    return this.browserService.showSupportChat() ? 'opened' : 'unavailable'
  }

  /** Sends a signed page update, or starts an anonymous session for a logged-out visitor. */
  private async initializeIntercom(): Promise<void> {
    if (this.account()?.isImpersonated) {
      return
    }

    const userId = this.currentUser()?.id

    if (this.appQuery.getIsLoggedIn()) {
      if (userId) {
        await this.identifyIntercomUser(userId)
      }

      return
    }

    if (!this.isIntercomAnonymous) {
      this.callIntercom('shutdown')
    }

    this.callIntercom(this.isIntercomAnonymous ? 'update' : 'boot', {
      ...this.intercomSettings,
      last_request_at: Math.floor(Date.now() / 1000)
    })
    this.isIntercomAnonymous = true
  }

  /**
   * Boots or updates Messenger with signed identity and optional browser telemetry.
   * @param {string} userId The current workspace user.
   * @param {Record<string, unknown>} [properties] Browser telemetry to preserve across refreshes.
   */
  private async identifyIntercomUser(userId: string, properties?: Record<string, unknown>): Promise<void> {
    if (!this.canIdentifyIntercomUser(userId)) {
      return
    }

    if (this.intercomTargetUserId !== userId || this.intercomTargetWorkspaceId !== this.workspace()?.id) {
      this.clearIntercomIdentity()
      this.intercomTargetUserId = userId
      this.intercomTargetWorkspaceId = this.workspace()?.id
    }

    if (properties) {
      // Browser-only telemetry is not trusted identity or subscription data.
      this.intercomBrowserAttributes = Object.fromEntries(
        ['os', 'osVersion', 'test_group', 'ab_test_group']
          .filter(key => properties[key] !== undefined)
          .map(key => [key, properties[key]])
      )
    }

    // Page views must not bypass a pending retry or a stop.
    if (Date.now() < this.intercomTokenPausedUntil) {
      return
    }

    const version = this.intercomIdentityVersion

    clearTimeout(this.intercomRefreshTimer)

    try {
      // Remove a persisted session before the first token request, including its failure path.
      if (!this.intercomUserId) {
        this.callIntercom('shutdown')
      }

      const result = await this.getIntercomUserJwt(userId)

      if (
        version !== this.intercomIdentityVersion ||
        !this.canIdentifyIntercomUser(userId) ||
        this.workspace()?.id !== this.intercomTargetWorkspaceId
      ) {
        return
      }

      if (result.kind === 'token') {
        this.bootIntercomWithToken(userId, result.token)
      } else if (result.kind === 'retry') {
        this.scheduleIntercomRetry(userId)
      } else {
        this.intercomTokenPausedUntil = Infinity
      }
    } catch {
      this.logService.logError(new Error('Unable to initialize Intercom Messenger'))

      if (version === this.intercomIdentityVersion) {
        this.scheduleIntercomRetry(userId)
      }
    }
  }

  /**
   * Boots or updates Messenger with a signed token and schedules the refresh before it expires.
   * @param {string} userId The current workspace user.
   * @param {SupportTokenResponse} token The signed token.
   */
  private bootIntercomWithToken(userId: string, token: SupportTokenResponse): void {
    this.callIntercom(this.intercomUserId === userId ? 'update' : 'boot', {
      ...this.intercomBrowserAttributes,
      ...this.intercomSettings,
      intercom_user_jwt: token.token,
      // Messenger needs changed data to send a ping when only the URL changes.
      last_request_at: Math.floor(Date.now() / 1000)
    })
    this.intercomUserId = userId
    this.intercomRetryDelay = INTERCOM_FIRST_RETRY_DELAY
    this.intercomTokenPausedUntil = 0

    const delay = Math.max(INTERCOM_FIRST_RETRY_DELAY, token.expiresAt * 1000 - Date.now() - 60_000)

    clearTimeout(this.intercomRefreshTimer)
    this.intercomRefreshTimer = setTimeout(() => {
      this.intercomUserJwt = undefined
      void this.identifyIntercomUser(userId)
    }, delay)
  }

  /**
   * Retries a failed token request later. The wait doubles after each failure, up to the normal refresh interval.
   * @param {string} userId The current workspace user.
   */
  private scheduleIntercomRetry(userId: string): void {
    // Callers that shared the failed request must not stretch the wait again.
    if (Date.now() < this.intercomTokenPausedUntil) {
      return
    }

    const delay = this.intercomRetryDelay

    this.intercomRetryDelay = Math.min(delay * 2, INTERCOM_MAX_RETRY_DELAY)
    this.intercomTokenPausedUntil = Date.now() + delay

    clearTimeout(this.intercomRefreshTimer)
    this.intercomRefreshTimer = setTimeout(() => {
      this.intercomTokenPausedUntil = 0
      void this.identifyIntercomUser(userId)
    }, delay)
  }

  /**
   * Checks that Messenger still represents the logged-in user.
   * @param {string} userId The expected workspace user.
   * @returns {boolean}
   */
  private canIdentifyIntercomUser(userId: string): boolean {
    return this.appQuery.getIsLoggedIn() && this.currentUser()?.id === userId && !this.account()?.isImpersonated
  }

  /** Invalidates pending Messenger work before an identity change. */
  private clearIntercomIdentity(): void {
    this.intercomIdentityVersion++
    clearTimeout(this.intercomRefreshTimer)
    this.intercomUserJwt = undefined
    this.intercomUserJwtPromise = undefined
    this.intercomUserId = undefined
    this.intercomBrowserAttributes = {}
    this.intercomRetryDelay = INTERCOM_FIRST_RETRY_DELAY
    this.intercomTokenPausedUntil = 0
    this.isIntercomAnonymous = false
    this.callIntercom('shutdown')
  }

  /**
   * Gets a token bound to the current workspace user. A response after logout is discarded.
   * Session recovery can complete without a response; treat it as a missing token.
   * @param {string} userId The expected workspace user.
   * @returns {Promise<SupportTokenResult>}
   */
  private async getIntercomUserJwt(userId: string): Promise<SupportTokenResult> {
    const workspaceId = this.workspace()?.id

    if (
      this.intercomUserJwt?.userId === userId &&
      this.intercomUserJwt.workspaceId === workspaceId &&
      this.intercomUserJwt.expiresAt * 1000 > Date.now() + 60_000
    ) {
      return { kind: 'token', token: this.intercomUserJwt }
    }

    if (this.intercomUserJwtPromise?.userId === userId) {
      return this.intercomUserJwtPromise.promise
    }

    const version = this.intercomIdentityVersion
    const promise = firstValueFrom(
      this.apiClient.get<SupportTokenResponse>(`${apiEndpoint}/me/supportToken`, { timeout: 10_000 }).pipe(take(1)),
      { defaultValue: null }
    )
      .then(response => {
        if (
          !response ||
          version !== this.intercomIdentityVersion ||
          response.userId !== userId ||
          response.workspaceId !== workspaceId ||
          !response.token ||
          response.expiresAt * 1000 <= Date.now()
        ) {
          return { kind: 'retry' } as const
        }

        this.intercomUserJwt = response

        return { kind: 'token', token: response } as const
      })
      .catch((error: unknown) => this.handleIntercomTokenError(error))

    this.intercomUserJwtPromise = { userId, promise }

    try {
      return await promise
    } finally {
      if (this.intercomUserJwtPromise?.promise === promise) {
        this.intercomUserJwtPromise = undefined
      }
    }
  }

  /**
   * Decides whether a failed token request is retried, and logs each failure status once per session.
   * Only the status is logged: the HTTP response can contain a bearer token.
   * @param {unknown} error The failed token request error.
   * @returns {SupportTokenResult}
   */
  private handleIntercomTokenError(error: unknown): SupportTokenResult {
    const status = error instanceof HttpErrorResponse ? error.status : undefined
    // ErrorInterceptor already handles session recovery and request diagnostics. Status 0 is offline or a client timeout.
    const isSilent = status === 0 || status === 401 || status === 403 || status === 499
    const reason = status === undefined ? 'unknown error' : `status ${status}`

    if (!isSilent && !this.loggedIntercomTokenFailures.has(reason)) {
      this.loggedIntercomTokenFailures.add(reason)
      this.logService.logError(
        new Error(`Unable to load Intercom identity token (${reason})`),
        undefined,
        undefined,
        ErrorType.External
      )
    }

    return { kind: this.isRetryableIntercomTokenStatus(status) ? 'retry' : 'stop' }
  }

  /**
   * Checks whether a token request can succeed later without an identity change.
   * @param {number | undefined} status The HTTP status, or undefined for a non-HTTP error.
   * @returns {boolean}
   */
  private isRetryableIntercomTokenStatus(status: number | undefined): boolean {
    if (status === undefined || status === 0 || status === 408 || status === 429 || status === 499) {
      return true
    }

    return status >= 500
  }

  /**
   * Checks why the Messenger widget script did not load, for example because of an ad blocker.
   * The SDK keeps a command queue in `window.Intercom` in that case, so the global alone does not show it.
   * The SDK can add the script later, after the document is ready, so the listener captures errors on the document.
   */
  private watchIntercomScript(): void {
    const onError = (event: Event) => {
      if (event.target instanceof HTMLScriptElement && event.target.id === INTERCOM_LOADER_SCRIPT_ID) {
        this.intercomScriptFailure = 'unavailable'
        void this.checkIntercomScriptBlocked(event.target.src)
      }
    }

    document.addEventListener('error', onError, true)
    this.destroyRef.onDestroy(() => document.removeEventListener('error', onError, true))
  }

  /**
   * Marks Messenger as blocked when an online browser also fails a second request for its script.
   * A CDN error still returns a response, and an offline browser has nothing to block.
   * The browser cannot tell an ad blocker from a DNS, TLS or connection failure, so the hint names both.
   * @param {string} src The Messenger script URL.
   */
  private async checkIntercomScriptBlocked(src: string): Promise<void> {
    if (!navigator.onLine) {
      return
    }

    try {
      await fetch(src, { mode: 'no-cors', cache: 'no-store' })
    } catch {
      this.intercomScriptFailure = 'blocked'
    }
  }

  /**
   * Invokes Messenger without letting SDK errors interrupt the app.
   * @param {string} command The SDK command.
   * @param {Record<string, unknown>} [payload] The SDK payload.
   */
  private callIntercom(command: string, payload?: Record<string, unknown>): void {
    try {
      if (command === 'boot' && payload) {
        const settings = { ...this.intercomSettings, ...payload }

        if (typeof Intercom !== 'function') {
          initializeMessenger(settings)
        } else {
          // Keep the initial settings current if the SDK script is still loading.
          window.intercomSettings = settings
          Intercom('boot', settings)
        }
      } else if (typeof Intercom === 'function') {
        if (command === 'shutdown') {
          window.intercomSettings = this.intercomSettings
        }

        if (payload) {
          Intercom(command, payload)
        } else {
          Intercom(command)
        }
      }
    } catch {
      this.logService.logError(new Error('Intercom command failed'))
    }
  }

  /**
   * Track Event API Call
   * @param {string} eventName
   * @param {object} data
   * @param {string} userId
   * @returns {Observable<void>}
   */
  trackEventCall(eventName: string, data: object, userId?: string): Observable<void> {
    const body: { eventName: string; data: object; context: object; userId?: string } = {
      eventName,
      data,
      context: this.setEventContextData()
    }

    if (userId) {
      body.userId = userId
    }

    return this.apiClient.post(`${apiEndpoint}/track`, body)
  }

  /**
   * Track Event Without Being Logged API Call
   * @param {TrackingEvent} eventName
   * @param {object} [data=object]
   * @returns {Observable<void>}
   */
  trackEventWithoutBeingLoggedIn(eventName: TrackingEvent, data: object = object): Observable<void> {
    const body: { eventName: string; data: object; context: object } = {
      eventName,
      data: {
        ...data,
        abTestGroup: this.abTestService.getTestGroupKey()
      },
      context: this.setEventContextData()
    }

    if (this.isDeveloperEnvironment()) {
      console.log(eventName, data)
    }

    return this.apiClient.post(`${apiEndpoint}/trackevent`, body)
  }

  /**
   * Function which tracks the signup started event. Only after this is done,
   * the further redirection is allowed to be done.
   * @param {string} userId
   * @param {string} email
   * @param {boolean} emailNotFound
   * @param {boolean} isPaidTrial
   * @returns {Promise<void>}
   */
  trackSignUpStarted(userId: string, email: string, emailNotFound?: boolean, isPaidTrial?: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // FALLBACK - if the tracking isn't done after 2 seconds, resolve the promise manually.
        const timeout = setTimeout(() => {
          resolve()
        }, 2000)

        // NOTE: Currently commented out because the website <-> awork tracking is inactive right now
        // If the user has already an account, reset the distinct Id to create a new user for tracking
        // if (!this.emailNotFound) {
        //   this.trackingService.resetDistinctId()
        // }
        // // Set the alias to convert the user from unknown to known
        // this.trackingService.setAlias(resp.userId)

        // Set the user to the creation user
        this.identifyUser(
          userId,
          {
            is_team_creator: true,
            device_category: this.browserService.getDeviceCategory()
          },
          () => {
            // Track Signup Event
            this.trackEvent(
              TrackingEvent.signedUp,
              {
                is_first_account: emailNotFound,
                sign_up_email: email
              },
              () => {
                clearTimeout(timeout)
                resolve()
              }
            )

            if (isPaidTrial) {
              this.trackEvent(TrackingEvent.trialStarted, object)
            }
          }
        )
      } catch (err) {
        resolve()
      }
    })
  }

  /**
   * Check if the user is in an specific segmentId
   * @param {TrackingSegment} segment
   * @returns {Observable<boolean>}
   */
  getIsInSegment(segment: TrackingSegment): Observable<boolean> {
    return this.apiClient
      .get<{ isInSegment: boolean }>(`${apiEndpoint}/me/intercom/isinsegment?segmentId=${segment}`)
      .pipe(map(response => response.isInSegment))
  }

  /**
   * Identifies the user and the group, and tracks the app accessed event
   */
  identifyAndTrackAppAccessed(): void {
    this.trackAppAccessedSubscription = forkJoin({
      user: this.signalStoreService.selectOnStoreRestored('user'),
      workspace: this.signalStoreService.selectOnStoreRestored('workspace'),
      permissions: this.signalStoreService.selectOnStoreRestored('permissions'),
      subscription: this.signalStoreService.selectOnStoreRestored('subscription')
    }).subscribe(() => {
      const user = this.userQuery.queryCurrentUser()
      const workspace = this.workspaceQuery.queryCurrentWorkspace()
      const permissions = this.permissionsQuery.queryUserPermissions()
      const accountSubscription = this.subscriptionQuery.querySubscription()

      if (!workspace() || !user()) {
        return
      }

      // Track plan info
      let plan = ''
      let period = ''
      let projects: number = null
      let quota: number = null
      let status = ''
      let trialEnd: Date

      const subdomain = workspace().getDefaultSubdomain()

      try {
        if (accountSubscription()) {
          if (accountSubscription().planId) {
            plan = accountSubscription().getPlan()
            period = accountSubscription().getPlanPeriod().toString()
          }

          quota = accountSubscription().bookedSeats
          status = accountSubscription().status
          projects = accountSubscription().totalProjects
          trialEnd = accountSubscription().trialEnd
        }
      } catch (ex) {}

      // Track identity
      const account: AccountState = this.accountQuery.getAccount()
      const origin_via_connect = !!accountSubscription()?.isInitialPlanConnect()

      const referralCode = this.appQuery.query('referralCode')

      // Track user info
      const userProperties = {
        // user info
        created_at: this.formatDate(user().createdOn),
        account_id: user().accountId,
        email: account.email,
        email_confirmed: account.emailConfirmed,
        username: account.email,
        is_admin: permissions() ? permissions().isAdmin : false,
        referral_code: referralCode(),
        origin_via_connect,

        // personal infos
        name: user().fullName,
        first_name: user().firstName,
        last_name: user().lastName,
        language: getLanguage(),
        gender: user().gender,
        age: differenceInYears(new Date(), user().birthDate),

        // system info
        os: this.browserService.getOS(),
        osVersion: this.browserService.getOSVersion(),

        // set up some test groups
        test_group: this.abTestService.isIntercomTestGroup(),
        ab_test_group: this.abTestService.getTestGroupKey(),

        // workspace info
        team_free_projects: projects,
        team_id: workspace().id,
        team_name: workspace().name,
        team_url: subdomain.name,
        team_created_at: this.formatDate(workspace().createdOn),
        team_plan_name: plan,
        team_plan_users: quota,
        team_plan_status: status,
        team_plan_period: period,
        team_trial_end: this.formatDate(trialEnd),
        team_age_days: differenceInDays(new Date(), workspace().createdOn)
      }

      // identify user
      this.identifyUser(user().id, userProperties)

      // identify group
      const groupProperties = {
        name: workspace().name,
        team_free_projects: projects,
        team_url: subdomain.name,
        team_plan_name: plan,
        team_plan_users: quota,
        team_plan_status: status,
        team_plan_period: period,
        team_trial_end: this.formatDate(trialEnd),
        team_age_days: differenceInDays(new Date(), workspace().createdOn),
        created_at: this.formatDate(workspace().createdOn),
        origin_via_connect
      }

      if (this.isLoggingSample) {
        this.logService.logMessage(
          Level.info,
          `Identify ${account.email} | origin_via_connect: ${origin_via_connect} | initialPlanId: ${accountSubscription()?.initialPlanId}`
        )
      }

      // identify company/workspace
      this.identifyGroup(workspace().id, groupProperties)

      // TODO: Set the user's location
      // this.locationService.setLocation()

      // Track awork event that the user accessed the app
      this.qEventService.trackAppAccessedEvent().subscribe()
    })
  }

  /**
   * Unsubscribe from the track app accessed subscription
   */
  stopTrackAppAccessed(): void {
    this.trackAppAccessedSubscription?.unsubscribe()
  }

  /**
   * Formats the date so all integrations are capable to read it.
   * Returns null for absent or invalid date values.
   * @param {Date | string | null | undefined} dateObj
   * @returns {string | null}
   */
  formatDate(dateObj: Date | string | null | undefined): string | null {
    if (!dateObj) {
      return null
    } else {
      const d = new Date(dateObj)

      if (isNaN(d.getTime())) {
        return null
      }

      return d.toISOString()
    }
    // other method which works for intercom, but not for mixpanel:
    // return Math.floor(dateObj.getTime() / 1000)
  }

  /**
   * Returns whether the app is running on localhost or in develop
   * @returns {boolean}
   */
  private isDeveloperEnvironment(): boolean {
    return environment === 'local' || environment === 'develop'
  }

  /**
   * Context event data
   * @returns {object}
   */
  private setEventContextData(): object {
    return {
      userAgent: navigator.userAgent,
      locale: navigator.language,
      os: this.browserService.getOS(),
      osVersion: this.browserService.getOSVersion(),
      page: {
        path: window.location.pathname,
        title: document.title,
        url: window.location.href,
        referrer: document.referrer
      }
    }
  }

  /**
   * Returns the marketing context for the tracking
   * @returns {{ 'marketing-values': Record<string, string>; }}
   * @private
   */
  private getMarketingTrackingContext(): {
    'marketing-values': Record<string, string>
  } {
    return {
      'marketing-values': buildMarketingPropertiesFromAwUrlCookieOrUrl({
        awUrlCookie: getCookie('aw_url'),
        url: window.location.href
      })
    }
  }
}
